import { useHandlerCompanionEnabled } from "@/features/stepper/steptypes/companion-config";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";

export interface MissionExample {
  glowId: string;
  agent: string;
  did: string;
  felt: string;
  media?: string;
}

/** One glow.missionbank row — the orders the kit is built from. */
export interface Mission {
  missionId: number;
  customId?: string;
  handler?: "Veronica" | "Darius";
  /** The mission as small steps, when authored that way (see
   *  steptypes/mission-steps.ts for what an older row gets). */
  steps?: string[];
  name: string;
  level: string;
  mission: string;
  helpText?: string;
  provenTruths: string[];
  /** The strike this mission counters (missionbank.Strike); "" when none. */
  strike?: string;
  doorNames: string[];
  examples?: MissionExample[];
}

export function useMission(missionId: string | number | null) {
  const companionEnabled = useHandlerCompanionEnabled();
  return useQuery({
    queryKey: [
      "mission",
      String(missionId),
      "bank-helptext-v2",
      companionEnabled,
    ],
    queryFn: async () => {
      if (String(missionId) === "0" && companionEnabled) {
        const { data } = await apiClient.get<{
          result: { flow: { phase: string; mission: Mission } | null };
        }>("/api/secretmissions/companion/mission/");
        if (data.result.flow?.phase !== "accepted")
          throw new Error("No accepted handler mission");
        return data.result.flow.mission;
      }
      const { data } = await apiClient.get<{
        success: boolean;
        result: Mission;
      }>(API_ENDPOINTS.mission.bank(missionId!));
      if (companionEnabled) {
        try {
          const personalized = await apiClient.get<{
            result: { suggestion: string };
          }>(API_ENDPOINTS.companion.mission(missionId!));
          if (personalized.data.result.suggestion) {
            return {
              ...data.result,
              mission:
                data.result.mission +
                "\n\nYour handler’s suggestion: " +
                personalized.data.result.suggestion,
            };
          }
        } catch {
          /* Personal context is optional; the original mission always works. */
        }
      }
      return data.result;
    },
    enabled: missionId != null && missionId !== "",
    // Playbook content — it doesn't change mid-run.
    staleTime: companionEnabled ? 0 : 60_000,
    gcTime: companionEnabled ? 0 : 5 * 60 * 1000,
    meta: { private: companionEnabled },
  });
}
